import React, { useState } from 'react';
import {
    Kanban,
    Calendar,
    Target,
    ChevronRight,
    Plus,
    Filter,
    CheckCircle2,
    Clock,
    LayoutPanelLeft,
    Zap
} from 'lucide-react';

const ProductivityView = () => {
    const [activeSection, setActiveSection] = useState('tasks');

    return (
        <div className="flex h-full bg-slate-50 animate-in fade-in duration-500">
            {/* Sidebar Navigation */}
            <div className="w-64 bg-white border-r border-slate-200 flex flex-col p-6 space-y-8">
                <div className="flex items-center gap-3 px-2">
                    <div className="p-2 bg-blue-600 rounded-lg text-white">
                        <Zap size={20} />
                    </div>
                    <h2 className="font-black text-slate-800 uppercase tracking-tight">Productivity</h2>
                </div>

                <nav className="space-y-1">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-2">Planning</p>
                    {[
                        { id: 'goals', label: 'Strategic Goals', icon: Target },
                        { id: 'milestones', label: 'Milestones', icon: Clock },
                        { id: 'tasks', label: 'Task Board', icon: Kanban },
                        { id: 'calendar', label: 'Schedule', icon: Calendar },
                    ].map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveSection(item.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all ${activeSection === item.id ? 'bg-blue-50 text-blue-700 shadow-sm' : 'text-slate-500 hover:bg-slate-50'}`}
                        >
                            <item.icon size={18} />
                            {item.label}
                        </button>
                    ))}
                </nav>

                <div className="mt-auto p-4 bg-slate-900 rounded-3xl text-white space-y-3">
                    <div className="flex items-center gap-2 text-xs font-bold opacity-80">
                        <Zap size={14} className="text-amber-400" />
                        <span>LifeOS Integration</span>
                    </div>
                    <p className="text-[10px] opacity-60 leading-relaxed">
                        Your holistic balance and energy levels are synced with your task load.
                    </p>
                    <button className="w-full py-2 bg-white/10 hover:bg-white/20 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors">
                        View Balance Map
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8">
                    <div className="flex items-center gap-2">
                        <h3 className="text-lg font-black text-slate-800 uppercase tracking-tight">
                            {activeSection === 'goals' && 'Strategic Goals'}
                            {activeSection === 'milestones' && 'Milestones'}
                            {activeSection === 'tasks' && 'Task Board'}
                            {activeSection === 'calendar' && 'Schedule'}
                        </h3>
                    </div>
                    <div className="flex items-center gap-3">
                        <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                            <Filter size={20} />
                        </button>
                        <button className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all active:scale-95 shadow-lg shadow-blue-200">
                            <Plus size={16} />
                            New Item
                        </button>
                    </div>
                </header>

                <main className="flex-1 overflow-auto p-8">
                    {activeSection === 'tasks' && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-full">
                            {['To Do', 'In Progress', 'Done'].map(column => (
                                <div key={column} className="flex flex-col gap-4">
                                    <div className="flex items-center justify-between px-2">
                                        <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">{column}</h4>
                                        <span className="text-[10px] font-bold bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">0</span>
                                    </div>
                                    <div className="flex-1 bg-slate-100/50 border-2 border-dashed border-slate-200 rounded-[2rem] p-4 space-y-4">
                                        {/* Sample Task */}
                                        <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm space-y-3 group hover:border-blue-200 transition-colors">
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2 h-2 bg-blue-500 rounded-full" />
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase">Ops</span>
                                                </div>
                                                <button className="text-slate-300 group-hover:text-slate-500 transition-colors">
                                                    <ChevronRight size={16} />
                                                </button>
                                            </div>
                                            <p className="text-sm font-bold text-slate-800">Refactor PWA View Architecture</p>
                                            <div className="flex items-center justify-between pt-2">
                                                <div className="flex items-center gap-1 text-slate-400">
                                                    <Clock size={12} />
                                                    <span className="text-[10px] font-medium">Due today</span>
                                                </div>
                                                <div className="flex -space-x-2">
                                                    <div className="w-5 h-5 rounded-full bg-slate-200 border-2 border-white" />
                                                    <div className="w-5 h-5 rounded-full bg-blue-200 border-2 border-white" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {activeSection !== 'tasks' && (
                        <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                            <div className="p-6 bg-slate-100 rounded-full text-slate-300">
                                <LayoutPanelLeft size={48} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-slate-800">Section Under Construction</h3>
                                <p className="text-slate-400 text-sm max-w-xs mx-auto">
                                    This part of the Linear replacement is being synchronized with the new 7-core-view architecture.
                                </p>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default ProductivityView;
